import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authService } from '../services/pocketbase.service';
import config from '../config/pocketbase.config';

// Create the context
const AuthContext = createContext(null);

// Custom hook for using the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState({ 
    isVerified: false, 
    shouldLogout: false, 
    timeRemaining: 0 
  });
  
  // Add synchronization state to prevent race conditions
  const [isInitializing, setIsInitializing] = useState(false);
  const initTimeoutRef = useRef(null);
  
  // Initialize authentication on app start
  const initializeAuth = useCallback(async () => {
    // Prevent multiple simultaneous initialization attempts
    if (isInitializing) {
      console.log('🔄 Auth initialization already in progress, skipping...');
      return;
    }
    
    // Clear any pending initialization timeout
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
    }
    
    // Debounce rapid initialization calls
    initTimeoutRef.current = setTimeout(async () => {
      try {
        console.log('🚀 Starting auth initialization...');
        setIsInitializing(true);
        setLoading(true);
        
        // First, try to get current user (which now checks localStorage)
        const currentUser = authService.getCurrentUser();
        console.log('👤 Current user from authService:', currentUser ? 'Found' : 'Not found');
        console.log('🔐 PocketBase isAuthenticated:', authService.isAuthenticated());
        console.log('🌐 Current domain:', window.location.hostname);
        
        if (currentUser) {
          // Check if this is an admin user - if so, don't set in AuthContext
          const collectionName = currentUser.collectionName;
          console.log('📋 User collection:', collectionName);
          
          if (collectionName === 'admins' || collectionName === '_admins' || 
              collectionName?.includes('admin')) {
            // This is an admin session, don't manage it in AuthContext
            console.log('👨‍💼 Admin user detected, not setting in AuthContext');
            setUser(null);
            setLoading(false);
            setIsInitializing(false);
            return;
          }
          
          console.log('✅ Setting user in AuthContext:', currentUser.username);
          setUser(currentUser);
          
          // If PocketBase auth store is not valid but we have user data, try to refresh
          if (!authService.isAuthenticated()) {
            console.log('🔄 Auth store invalid, attempting refresh...');
            try {
              // Try to refresh the token to restore the auth session
              const refreshResult = await authService.refreshAuth();
              if (refreshResult.success) {
                console.log('✅ Token refresh successful');
                setUser(refreshResult.user);
              } else {
                console.log('❌ Token refresh failed, clearing stored data');
                // If refresh fails, clear the stored user data
                localStorage.removeItem(config.auth.userStorageKey);
                setUser(null);
              }
            } catch (refreshError) {
              console.error('❌ Token refresh failed during initialization:', refreshError);
              // Clear invalid stored data
              localStorage.removeItem(config.auth.userStorageKey);
              setUser(null);
            }
          } else {
            console.log('✅ Auth store valid, performing background refresh...');
            // Auth store is valid, optionally refresh in background
            authService.refreshAuth()
              .then(result => {
                if (result.success && !isInitializing) {
                  console.log('✅ Background refresh successful');
                  setUser(result.user);
                }
              })
              .catch(() => {
                console.log('⚠️ Background refresh failed, keeping current state');
                // If refresh throws an error, keep existing user state to avoid logout flicker
                // Optionally log the error and rely on subsequent interactions to re-auth
              });
          }
        } else {
          console.log('❌ No current user found');
        }
      } catch (err) {
        console.error('❌ Auth initialization error:', err);
        setError('Failed to initialize authentication');
      } finally {
        console.log('🏁 Auth initialization complete');
        setLoading(false);
        setIsInitializing(false);
      }
    }, 100); // 100ms debounce
  }, [isInitializing]);
  
  // Set up automatic token refresh
  useEffect(() => {
    let refreshInterval;
    
    if (user) {
      // Check if current user is an admin - if so, don't set up refresh
      const currentUser = authService.getCurrentUser();
      if (currentUser && currentUser.collectionName) {
        const collectionName = currentUser.collectionName;
        if (collectionName === 'admins' || collectionName === '_admins' || 
            collectionName?.includes('admin')) {
          // Skip refresh for admin users
          return;
        }
      }
      
      refreshInterval = setInterval(() => {
        authService.refreshAuth()
          .then(result => {
            if (result.success) {
              setUser(result.user);
            }
          })
          .catch(() => {
            // On refresh error, keep current user; we can stop refreshing silently
            if (refreshInterval) clearInterval(refreshInterval);
          });
      }, config.auth.refreshInterval);
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user]);
  
  // Initialize auth on component mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);
  
  // Login function
  const login = async (identifier, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.login(identifier, password);
      
      if (result.success) {
        setUser(result.user);
        return { success: true, user: result.user };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  
  // Register function
  const register = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.register(userData);
      
      if (result.success) {
        setUser(result.user);
        return { success: true, user: result.user };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Register function without auto-login (for email verification flow)
  const registerWithoutLogin = async (userData) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await authService.registerWithoutLogin(userData);
      
      if (result.success) {
        return { success: true, user: result.user };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = () => {
    setLoading(true);
    
    try {
      authService.logout();
      setUser(null);
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Logout failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Simplified verification status - no time expiration
  const checkEmailVerification = useCallback(() => {
    if (!user) return { isVerified: false, shouldLogout: false, timeRemaining: 0 };
    return { isVerified: true, shouldLogout: false, timeRemaining: 0 };
  }, [user]);
  
  // Update user profile
  const updateProfile = async (updateData) => {
    if (!user) {
      return { success: false, error: 'No authenticated user' };
    }
    
    setLoading(true);
    
    try {
      const result = await authService.updateProfile(user.id, updateData);
      
      if (result.success) {
        setUser(result.user);
        return { success: true, user: result.user };
      } else {
        setError(result.message);
        return { success: false, error: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Profile update failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };
  
  // Context value
  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    verificationStatus,
    login,
    register,
    registerWithoutLogin,
    logout,
    updateProfile,
    refreshAuth: initializeAuth,
    checkEmailVerification
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;